/**
 * OperationalLineageNodeRenderer
 *
 * Deterministic SVG node rendering for each LineageNodeType.
 * Color and shape derived exclusively from operationalVisualLanguage tokens.
 * No random placement. No hidden enrichment.
 */

import React from 'react';
import type { LineageNode, LineageNodeType } from '../operationalLineageTypes.js';
import { NEUTRAL, SEMANTIC, TYPOGRAPHY } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { TRANSITION_GRAPH_NODE } from '../../motion/motionTokens.js';

export interface LayoutNode extends LineageNode {
  x: number;
  y: number;
}

const NODE_TYPE_LAYER: Record<LineageNodeType, number> = {
  event_stream: 0,
  projection: 1,
  replay_window: 1,
  pane_model: 2,
  continuity_state: 2,
  bundle: 3,
  attestation: 4,
  verifier_result: 4,
};

const NODE_WIDTH = 120;
const NODE_HEIGHT = 32;
const LAYER_HEIGHT = 90;
const LAYER_SPACING_X = 160;

function getNodeColor(node: LineageNode): { stroke: string; fill: string; text: string; glyph: string } {
  switch (node.type) {
    case 'event_stream':
      return { stroke: SEMANTIC.informational.border, fill: '#101420', text: SEMANTIC.informational.text, glyph: SEMANTIC.informational.glyph };
    case 'projection':
      return { stroke: NEUTRAL.borderStrong, fill: NEUTRAL.bg300, text: NEUTRAL.text500, glyph: NEUTRAL.text400 };
    case 'pane_model':
      return { stroke: NEUTRAL.border, fill: NEUTRAL.bg200, text: NEUTRAL.text400, glyph: NEUTRAL.text300 };
    case 'continuity_state': {
      const status = (node.metadata.currentStatus as string) || 'unknown';
      const color = SEMANTIC[status] || SEMANTIC.unknown;
      return { stroke: color.border, fill: '#101810', text: color.text, glyph: color.glyph };
    }
    case 'replay_window':
      return { stroke: SEMANTIC.replayWindow.border, fill: '#0a0e16', text: SEMANTIC.replayWindow.text, glyph: SEMANTIC.replayWindow.glyph };
    case 'bundle':
      return { stroke: NEUTRAL.borderStrong, fill: '#141418', text: NEUTRAL.text600, glyph: NEUTRAL.text500 };
    case 'attestation':
      return { stroke: SEMANTIC.admissible.border, fill: '#0c1410', text: SEMANTIC.admissible.text, glyph: SEMANTIC.admissible.glyph };
    case 'verifier_result': {
      const valid = node.metadata.verificationValid as boolean | undefined;
      const color = valid === false ? SEMANTIC.refusal : SEMANTIC.stable;
      return { stroke: color.border, fill: '#0c1410', text: color.text, glyph: color.glyph };
    }
    default:
      return { stroke: NEUTRAL.border, fill: NEUTRAL.bg200, text: NEUTRAL.text400, glyph: NEUTRAL.text300 };
  }
}

function getNodeShape(type: LineageNodeType): string {
  switch (type) {
    case 'event_stream': return '◆';
    case 'projection': return '■';
    case 'pane_model': return '▪';
    case 'continuity_state': return '●';
    case 'replay_window': return '◈';
    case 'bundle': return '◆';
    case 'attestation': return '◉';
    case 'verifier_result': return '◎';
    default: return '●';
  }
}

function getHashFragment(hash: string | null): string {
  if (!hash) return '—';
  return hash.length > 12 ? hash.slice(0, 12) + '…' : hash;
}

interface NodeRendererProps {
  node: LayoutNode;
  isSelected: boolean;
  isHovered: boolean;
  onClick: (node: LayoutNode) => void;
  onMouseEnter: (node: LayoutNode) => void;
  onMouseLeave: () => void;
}

export const OperationalLineageNodeRenderer: React.FC<NodeRendererProps> = ({
  node,
  isSelected,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const color = getNodeColor(node);
  const shape = getNodeShape(node.type);
  const label = node.label.length > 20 ? node.label.slice(0, 20) + '…' : node.label;

  const baseStroke = isSelected ? '#d8d8e8' : color.stroke;
  const baseStrokeWidth = isSelected ? 2 : 1;
  const fillOpacity = isHovered ? 0.9 : 0.7;

  return (
    <g
      transform={`translate(${node.x - NODE_WIDTH / 2}, ${node.y - NODE_HEIGHT / 2})`}
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(node)}
      onMouseEnter={() => onMouseEnter(node)}
      onMouseLeave={onMouseLeave}
      data-testid={`lineage-graph-node-${node.type}`}
    >
      {/* Node background */}
      <rect
        x={0}
        y={0}
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={4}
        ry={4}
        fill={color.fill}
        stroke={baseStroke}
        strokeWidth={baseStrokeWidth}
        fillOpacity={fillOpacity}
        style={{ transition: TRANSITION_GRAPH_NODE }}
      />
      {/* Type glyph */}
      <text
        x={10}
        y={NODE_HEIGHT / 2 + 1}
        fontSize={10}
        fill={color.glyph}
        fontFamily="monospace"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ pointerEvents: 'none' }}
      >
        {shape}
      </text>
      {/* Label */}
      <text
        x={20}
        y={NODE_HEIGHT / 2 - 1}
        fontSize={TYPOGRAPHY.micro.size}
        fill={color.text}
        fontFamily="system-ui, sans-serif"
        fontWeight={TYPOGRAPHY.label.weight}
        dominantBaseline="middle"
        style={{ pointerEvents: 'none' }}
      >
        {label}
      </text>
      {/* Hash fragment */}
      <text
        x={20}
        y={NODE_HEIGHT / 2 + 9}
        fontSize={9}
        fill={NEUTRAL.text300}
        fontFamily="monospace"
        dominantBaseline="middle"
        style={{ pointerEvents: 'none' }}
      >
        {getHashFragment(node.hash)}
      </text>
      {/* Selected indicator dot */}
      {isSelected && (
        <circle
          cx={NODE_WIDTH - 6}
          cy={6}
          r={3}
          fill="#d8d8e8"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );
};

/**
 * Deterministic layout: assign each node to a layer by type,
 * then compute x/y positions deterministically from sorted node IDs.
 */
export function computeDeterministicLayout(nodes: LineageNode[]): LayoutNode[] {
  const nodesWithLayer = nodes.map((n) => ({
    ...n,
    layer: NODE_TYPE_LAYER[n.type] ?? 0,
  }));

  // Group by layer and sort by ID for determinism
  const layers = new Map<number, typeof nodesWithLayer>();
  for (const n of nodesWithLayer) {
    const arr = layers.get(n.layer) ?? [];
    arr.push(n);
    layers.set(n.layer, arr);
  }

  const layoutNodes: LayoutNode[] = [];

  for (const [layer, layerNodes] of [...layers.entries()].sort((a, b) => a[0] - b[0])) {
    const sorted = [...layerNodes].sort((a, b) => a.id.localeCompare(b.id));
    const count = sorted.length;
    const totalWidth = (count - 1) * LAYER_SPACING_X;
    const startX = totalWidth / 2;

    for (let i = 0; i < count; i++) {
      const x = startX - (count - 1 - i) * LAYER_SPACING_X;
      const y = layer * LAYER_HEIGHT + 40;
      layoutNodes.push({ ...sorted[i], x, y });
    }
  }

  return layoutNodes;
}

export { NODE_WIDTH, NODE_HEIGHT, LAYER_HEIGHT, LAYER_SPACING_X };
